import { MEVETH_CONTRACT, MEVETH_ROUTER_CONTRACT, WETH_CONTRACT } from '@/constants/addresses';
import { useDebounce } from '@/hooks/useDebounce';
import {
  stakedAmount,
  stakedTxHash,
  stakedTxStatus,
  stakedTxSuccessMessage,
  writeTxErrorMessage,
} from '@/pages/_app';
import { device } from '@/styles/common';
import { captureCheckIn, captureException } from '@sentry/nextjs';
import { formatEther } from 'ethers/lib/utils.js';
import { AnimatePresence, motion } from 'framer-motion';
import { useAtom } from 'jotai';
import { use, useEffect, useState } from 'react';
import styled from 'styled-components';
import { parseEther, UserRejectedRequestError } from 'viem';
import {
  erc20ABI,
  erc4626ABI,
  useAccount,
  useBalance,
  useContractRead,
  useContractWrite,
  useFeeData,
  useNetwork,
  usePrepareContractWrite,
  useWaitForTransaction,
} from 'wagmi';
import MevEthAbi from '../../../MevEthAbi';
import { MevEthRouterAbi } from '../../../MevEthRouterAbi';
import BigWhiteButton from '../general/BigWhiteButton';
import {
  AmountContainer,
  AnimatedSpacerContainer,
  CenterFoldContainer,
  FoldInput,
  FoldSymbol,
  MaxButton,
  TopText,
  WethSwitch,
} from '../general/CenterFold';
import MEVETHLogo from '../general/MEVETHLogo';
import { explainerMap } from '../withdrawals/StakeCenterFold';
import ConfirmStakeView from './ConfirmStakeView';
import { PossibleModes } from './StakeTopFold';

type Props = {
  setMode: (mode: PossibleModes) => void;
  mode: PossibleModes;
};

function EthereumStake({ setMode, mode }: Props) {
  const { address } = useAccount();

  const { data: feeData, isError: feeDataError, isLoading: isFeeDataLoading } = useFeeData();
  // TODO: get this from the contract
  const [amount, setAmount] = useAtom(stakedAmount);
  const [maxAmount, setMaxAmount] = useState<string>('0.0');
  const debouncedAmount = useDebounce(amount, 500);
  const [hash, setHash] = useAtom(stakedTxHash);
  const [txErrorMessage, setTxErrorMessage] = useAtom(writeTxErrorMessage);
  const [txStatus, setTxStatus] = useAtom(stakedTxStatus);
  const [useWeth, setUseWeth] = useState(false);
  const [amountOut, setAmountOut] = useState(0n);
  const { data: userWethBalance, error: errorWethBalance } = useContractRead({
    address: WETH_CONTRACT,
    abi: erc4626ABI,
    functionName: 'balanceOf',
    args: [address || '0x0'],
    enabled: Boolean(address),
    watch: true,
  });

  const getMaxPayableAmount = () => {
    console.log('feeData', feeData);
    console.log('feedata config', config);
    let currentMaxAmount = maxAmount;
    if (useWeth && userWethBalance) {
      currentMaxAmount = formatEther(userWethBalance);
    }
    if (config && feeData?.maxFeePerGas) {
      return (
        parseEther(currentMaxAmount) -
        (BigInt(105000) * BigInt(feeData.maxFeePerGas) * BigInt(1)) / BigInt(1)
      );
    }
    return parseEther(currentMaxAmount);
  };
  const { data, isSuccess } = useBalance({
    address: address,
  });

  const safeParseEther = (eth: string) => {
    try {
      return parseEther(eth);
    } catch (error) {
      console.log(error);
      captureException(new Error('Failed parsing ETH'), {
        tags: {
          string: eth,
        },
      });
    }

    return parseEther('0');
  };

  const { data: amountOutStakeData, error: errorAmountOutStake } = useContractRead({
    address: MEVETH_ROUTER_CONTRACT,
    abi: MevEthRouterAbi,
    functionName: 'amountOutStake',
    args: [safeParseEther(amount)],
    enabled: Boolean(safeParseEther(amount) > 0),
    watch: true,
  });
  console.log('amountOutStakeData', amountOutStakeData);
  useEffect(() => {
    const data: any[] = amountOutStakeData as unknown as any[];
    if (data && data.length > 0 && data[0]) {
      setAmountOut(data[0] as unknown as bigint);
    }
  }, [amountOutStakeData]);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //if (safeParseEther(e.target.value) > safeParseEther(maxAmount)) return;
    if (!e.target.value || parseFloat(e.target.value) < 0) {
      setAmount('0');
    }
    let isHigerThanMaxAmount = false;
    let isLowerThanMaxBalance = false;
    try {
      isHigerThanMaxAmount = safeParseEther(e.target.value) > getMaxPayableAmount();
      isLowerThanMaxBalance = safeParseEther(e.target.value) <= safeParseEther(maxAmount);
    } catch (error) {
      console.log(error);
    }

    setAmount(e.target.value);
  };
  const onClickMax = () => {
    if (!address) return;
    const maxPrice = getMaxPayableAmount();
    if (maxPrice <= 0) {
      setAmount('0');
      return;
    }
    console.log('mm', maxPrice.toString(), safeParseEther(maxAmount).toString());
    let currentMaxAmount = maxAmount;
    if (useWeth && userWethBalance) {
      currentMaxAmount = formatEther(userWethBalance);
      setAmount(currentMaxAmount);
      return;
    }
    if (maxPrice < safeParseEther(currentMaxAmount)) {
      setAmount(formatEther(maxPrice));
    } else {
      setAmount(currentMaxAmount);
    }

    return;
  };
  const isMinAmountValid = () => {
    return safeParseEther(amount) >= safeParseEther('0.01');
  };
  const { config: configApproveWeth, error: errorApproveWeth } = usePrepareContractWrite({
    address: WETH_CONTRACT,
    abi: erc20ABI,
    functionName: 'approve',
    args: [MEVETH_ROUTER_CONTRACT, safeParseEther(amount)],
    enabled: Boolean(debouncedAmount) && isMinAmountValid() && useWeth,
  });
  const {
    data: writeDataApproveWeth,
    write: writeApproveWeth,
    isLoading: isLoadingApproveWeth,
    isSuccess: isWriteSuccessApproveWeth,
    isError: isErrorApproveWeth,
    error: writeApproveWethError,
  } = useContractWrite(configApproveWeth);
  const {
    data: dataApproveWethTx,
    isError: isApproveWethErrorTx,
    isLoading: isLoadingTxApproveWeth,
    isSuccess: isSuccessTxApproveWeth,
    isFetching,
  } = useWaitForTransaction({
    hash: writeDataApproveWeth?.hash,
    enabled: Boolean(writeDataApproveWeth?.hash),
    timeout: 120_000,
  });

  const { config, error } = usePrepareContractWrite({
    address: MEVETH_ROUTER_CONTRACT,
    abi: MevEthRouterAbi,
    functionName: 'stakeEthForMevEth',
    args: [
      address,
      safeParseEther(amount),
      amountOut - (amountOut * 1n) / 100n,
      (new Date(new Date().getTime() + 10 * 60000).getTime() / 1000).toFixed(0),
      //@ts-ignore
      amountOutStakeData ? amountOutStakeData[1] : {},
    ],
    value: useWeth ? BigInt(0) : safeParseEther(amount),
    enabled: Boolean(debouncedAmount) && amountOut > 0n,
  });

  const {
    data: writeData,
    write,
    isLoading,
    isSuccess: isWriteSuccess,
    isError,
    isIdle,
    error: writeError,
    reset,
  } = useContractWrite(config);
  const {
    data: dataTx,
    isError: isErrorTx,
    isLoading: isLoadingTx,
    isSuccess: isSuccessTx,
  } = useWaitForTransaction({
    hash: writeData?.hash,
    enabled: Boolean(writeData?.hash),
  });

  console.log('bool', Boolean(useWeth));
  const {
    data: wethAllowance,
    error: errorWethAllowance,
    isSuccess: isSuccessAllowance,
    isLoading: isLoadingAllowance,
  } = useContractRead({
    address: WETH_CONTRACT,
    abi: erc20ABI,
    functionName: 'allowance',
    args: [address || '0x0', MEVETH_ROUTER_CONTRACT],
    enabled: Boolean(useWeth) && Boolean(userWethBalance),
    watch: true,
  });
  if (isLoadingAllowance) {
    console.log('loading weth allowance', wethAllowance);
  }
  if (isSuccessAllowance) {
    console.log('success weth allowance', wethAllowance);
  }
  if (isWriteSuccess) {
    if (writeData?.hash) {
      setHash(writeData.hash);
    }
  }
  if (isLoading) {
    setMode('Confirm');
    console.log('status', 'Loading', writeData?.hash);

    if (writeData?.hash) {
      setTxStatus('Loading');
      setHash(writeData.hash);
    }
  }

  if (isError) {
    if (writeError?.cause instanceof UserRejectedRequestError) {
      //just go back to staking screen if user rejects wallet tx
      setMode('Stake');
      setTxStatus('None');
      console.log('user rejected');
    } else if (writeError) {
      //show message
      setMode('Confirm');
      setTxStatus('Error');
      setTxErrorMessage(writeError.message);
      captureException(new Error('Failed deposit'), {
        tags: {
          message: writeError.message,
        },
      });
    }
  }
  if (isErrorApproveWeth) {
    if (writeApproveWethError?.cause instanceof UserRejectedRequestError) {
      //just go back to staking screen if user rejects wallet tx
      setMode('Stake');
      setTxStatus('None');
    } else if (writeApproveWethError) {
      //show message
      setMode('Confirm');
      setTxStatus('Error');
      setTxErrorMessage(writeApproveWethError.message);
      captureException(new Error('❌ Failed WETH9 Approval'), {
        tags: {
          message: writeApproveWethError.message,
        },
      });
    }
  }

  if (isSuccessTx) {
    switch (dataTx?.status) {
      case 'success':
        setTxStatus('Success');
        break;
      case 'reverted':
        setTxStatus('Error');
        setTxErrorMessage('Your transaction has been reverted, no funds have been deposited.');
        break;
      default:
        break;
    }
  }
  const onClickStake = async () => {
    if (!useWeth && !isMinAmountValid()) {
      return;
    }
    if (address) {
      if (isSuccess && write) {
        if (!error) {
          try {
            await write();
          } catch (error) {
            setMode('Confirm');
            setTxStatus('Error');
            const message: string = error as unknown as string;
            setTxErrorMessage(message);
            captureException(new Error('Failed staling write transaction'), {
              tags: {
                message,
              },
            });
          }
        } else {
          console.log('error', error);
        }
      } else {
        console.log('error', isError, writeError, isSuccess, write);
      }
    } else {
      alert('no ethers');
    }
  };
  const onClickApprove = async () => {
    if (!isMinAmountValid()) {
      return;
    }
    if (writeApproveWeth) {
      try {
        await writeApproveWeth(); //, { args: [amount, address] });
      } catch (error) {
        setMode('Confirm');
        setTxStatus('Error');
        const message: string = error as unknown as string;
        setTxErrorMessage(message);
        captureException(new Error('❌ Failed WETH9 approval write transaction'), {
          tags: {
            message,
          },
        });
      }
    }
  };
  useEffect(() => {
    if (isSuccess && data?.formatted) {
      data.value;
      setMaxAmount(data.formatted);
    }
  }, [isSuccess, data?.formatted]);

  if (mode === 'Confirm' && (isLoading || isError || isSuccess)) {
    console.log('modemode', mode);

    //! TODO: Copywriting - Fix Content
    return <ConfirmStakeView setMode={setMode} reset={reset} />;
  } else if (mode === 'Stake') {
    return (
      <CenterFoldContainer>
        <TopText>
          Stake ETH in your wallet to receive mevETH, a Liquid Staking Token (LST) that corresponds
          to your staked ETH, enabling seamless participation in Ethereum's Proof of Stake network
          using our{' '}
          <a
            target="_blank"
            rel="noreferrer"
            href="https://www.rated.network/o/MevEth?timeWindow=all&network=mainnet&idType=pool"
          >
            top-tier rated validators
          </a>
          .
        </TopText>
        <AnimatedSpacerContainer>
          <AmountContainer>
            <FoldInput
              value={amount}
              onChange={onChange}
              type="number"
              max={maxAmount}
              placeholder="0.0"
            />
            <FoldSymbol>
              <MEVETHLogo height={32} width={32} />
            </FoldSymbol>
          </AmountContainer>
          {useWeth && userWethBalance ? (
            <MaxButton onClick={onClickMax}>Max: {formatEther(userWethBalance)} WETH</MaxButton>
          ) : (
            <MaxButton onClick={onClickMax}>Max: {maxAmount} ETH</MaxButton>
          )}
          {userWethBalance ? (
            useWeth ? (
              <WethSwitch
                onClick={() => {
                  setUseWeth(false);
                }}
              >
                Switch to ETH ({maxAmount} ETH)
              </WethSwitch>
            ) : (
              <WethSwitch
                onClick={() => {
                  setUseWeth(true);
                }}
              >
                Switch to WETH ({formatEther(userWethBalance)} WETH)
              </WethSwitch>
            )
          ) : null}
        </AnimatedSpacerContainer>
        {safeParseEther(amount) === BigInt(0) || isMinAmountValid() ? (
          useWeth ? (
            safeParseEther(amount) > 0 &&
            userWethBalance &&
            wethAllowance &&
            wethAllowance >= safeParseEther(amount) ? (
              <BigWhiteButton
                text={`Stake ${parseFloat(amount).toFixed(2)} WETH for ${parseFloat(
                  formatEther(amountOut),
                ).toFixed(4)} mevETH`}
                onClick={onClickStake}
              />
            ) : (
              <BigWhiteButton
                text={
                  isLoadingTxApproveWeth
                    ? 'Approving... please wait.'
                    : !!userWethBalance && userWethBalance < safeParseEther(amount)
                    ? 'You do not have enough WETH'
                    : 'Approve mevETH to deposit your WETH'
                }
                disabled={
                  isLoadingApproveWeth ||
                  isLoadingTxApproveWeth ||
                  isFetching ||
                  amount === '' ||
                  amount === '0' ||
                  (!!userWethBalance && userWethBalance < safeParseEther(amount))
                }
                onClick={onClickApprove}
              />
            )
          ) : (
            <BigWhiteButton
              text={
                safeParseEther(amount) > safeParseEther(maxAmount)
                  ? 'You do not have enough ETH'
                  : `Stake ${parseFloat(amount).toFixed(2)} ETH for ${parseFloat(
                      formatEther(amountOut),
                    ).toFixed(4)} mevETH`
              }
              onClick={onClickStake}
              disabled={safeParseEther(amount) > safeParseEther(maxAmount)}
            />
          )
        ) : (
          <BigWhiteButton text="Stake at least 0.01 ETH" disabled={true} onClick={onClickStake} />
        )}
      </CenterFoldContainer>
    );
  }

  return <UseMevETH />;
}

function UseMevETH() {
  const [selectedProtocol, setSelectedProtocol] = useState('');

  return (
    <CenterFoldContainer>
      <TopText>You can use your mevETH to earn rewards on other protocols. </TopText>
      <AnimatedSpacerContainer>
        <LogosRow>
          {explainerMap.map((explainer) => (
            <a href={explainer.href} rel="noopener" target="_blank">
              <Partner>
                <SVGImage isActive={selectedProtocol === 'SushiSwap'} src={explainer.src} />
                <PartnerDetails>{explainer.desc}</PartnerDetails>
              </Partner>
            </a>
          ))}
        </LogosRow>
      </AnimatedSpacerContainer>
      {/**
      <AnimatedSpacerContainer>
      * 
      *  <LogosRow>
          <Partner>
            <a href="https://sushi.com" rel="noopener" target="_blank">
              <SVGImage
                onClick={() => setSelectedProtocol('SushiSwap')}
                isActive={selectedProtocol === 'SushiSwap'}
                src="/svg/sushi.svg"
              />
            </a>
            <PartnerDetails>{explainerMap.SushiSwap}</PartnerDetails>
          </Partner>
          <Partner>
            <a href="https://aave.com" rel="noopener" target="_blank">
              <SVGImage
                src="/svg/aave.svg"
                onClick={() => setSelectedProtocol('Aave')}
                isActive={selectedProtocol === 'Aave'}
              />
            </a>
            <PartnerDetails>{explainerMap.Aave}</PartnerDetails>
          </Partner>
          <Partner>
            <a href="https://ribbon.finance" rel="noopener" target="_blank">
              <SVGImage
                src="/svg/ribbon.svg"
                onClick={() => setSelectedProtocol('Ribbon')}
                isActive={selectedProtocol === 'Ribbon'}
              />
            </a>
            <PartnerDetails>{explainerMap.Ribbon}</PartnerDetails>
          </Partner>
        </LogosRow> 
      * 
      </AnimatedSpacerContainer>

      */}
    </CenterFoldContainer>
  );
}

const PartnerDetails = styled.div`
  margin-left: 10px;
  margin-top: 10px;
`;
const Partner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: left;
  margin-top: 10px;
  align-items: left;
`;

const LogosRow = styled.div`
  justify-content: left;
  align-items: left;
  gap: 24px;
`;

const SVGImage = styled.img<{ isActive: boolean }>`
  height: 40px;
  width: 40px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  &:hover {
    ${({ isActive }) =>
      !isActive &&
      'opacity: ${({ isActive }) => (isActive ? 1 : 0.8)}; cursor: pointer; transform: scale(1.05)'}
  }

  transition: all 0.2s ease-in-out;
`;

const ExplainerText = styled(motion.div)`
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  height: 48px;
  margin-top: 16px;

  @media ${device.mobileS} {
    font-size: 14px;
  }
`;
export default function StakeCenterFold({ setMode, mode }: Props) {
  return <EthereumStake setMode={setMode} mode={mode} />;
}
